import React, { useState, useRef, useEffect } from "react";
import { UiComponents, UnoStoreHooks, PasswordKey } from "@simulanisindia/ui";
import { AddIcon, defaultLogo, DeleteIcons, UploadIcon } from "../Icons";
import RadioOptions from "../components/RadioOptions";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { CREATE_USER, UPDATE_USER, UPLOAD_USER_AVATAR, USER_DETAILS } from "../apiClient/apiEndpoints";
import axios from "axios";
import { debounce, VALIDATE_REGEX, isValidFile, modalName, validatePasswordChecker, sanitizeString, sanitizeWithRemoveSpace } from "../utils/general";
import apiClient from "../apiClient";
import { useModalContext } from "./context/ModalProvider";
import BtnLoad from "../components/BtnLoad";
import PhoneInput from "react-phone-input-2";
import { ACCEPTED_IMAGE_TYPES } from "../constant/constants";
import PhoneNumberInput from "../components/PhoneNumberInput";

const { SimInputBar, SimToggleSwitch, SimTooltip, DropdownSearch } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

let userLoginTypes = [
  {
    label: "Email",
    value: "email",
    key: "email",
  },
  {
    label: "UID/Password",
    value: "uid",
    key: "uid",
  },
];

const UserAddData = ({ isMobileView }) => {
  const configData = useUnoSelector((state) => state.config);
  let passwordPolicy = configData.config.passwordPolicy;
  const { modalState, openModal, closeModal, handleLoading } = useModalContext();
  const { params, type, id, handleRefreshTab, openChangePasswordModal } = modalState;
  const { uuid } = params;
  const userData = useUnoSelector((state) => state.user);
  const configSlice = useSelector((state) => state.configSlice);
  const fileInputRef = useRef(null);
  const [roleTypes, setRoleTypes] = useState([]);

  const [userDetails, setUserDetails] = useState({
    name: "",
    emailOrUID: "",
    phone: "",
    designation: "",
    avatar: "",
    password: "",
    role: null,
    active: true,
    loginType: "email",
    phoneNumberError: false,
  });

  const [error, setError] = useState({
    name: null,
    emailOrUID: "",
    phone: "",
    password: "",
    role: null,
    submitError: null,
    file: null,
  });

  const resetUserDetails = () => {
    setUserDetails({
      name: "",
      emailOrUID: "",
      phone: "",
      designation: "",
      avatar: "",
      password: "",
      role: null,
      active: true,
      loginType: "email",
    });
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCloseModal = () => {
    closeModal();
    setTimeout(() => {
      resetUserDetails();
    }, 2000);
  };

  const handleOpenChangePasswordModal = () => {
    openChangePasswordModal();
  };

  const handleLogoUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (isValidFile(file, ACCEPTED_IMAGE_TYPES)) {
        setError((prev) => ({ ...prev, file: null }));
      } else {
        setError((prev) => ({ ...prev, file: "Invalid Image" }));
        return;
      }
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios.post(`${configData.config.UNO_URL}${UPLOAD_USER_AVATAR}/${uuid}`, formData, {
        headers: {
          Authorization: `Bearer ${userData?.token}`,
          "Content-Type ": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          // let uploadProgress = Math.floor(progress);
          // uploadProgress(file, Math.floor(progress));
        },
      });

      setUserDetails((pre) => ({ ...pre, avatar: data.url }));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    let typeList = configSlice.roles.map((role) => {
      return { value: role.id, label: role.name };
    });
    setRoleTypes(typeList);
  }, [configSlice]);

  const getDetails = async (id) => {
    try {
      let data = await apiClient.get(`${configData.config.UNO_URL}${USER_DETAILS}/${id}`);
      const roleAllocation =
        data?.roleAllocations.length > 1
          ? data.roleAllocations.filter((e) => e.role.type !== "DEFAULT")[0]
          : data.roleAllocations[0];
      const { role } = roleAllocation;
      setUserDetails({
        name: data.name,
        emailOrUID: data.email || data.uid,
        phone: data.phone,
        designation: data.designation,
        avatar: data.avatar,
        password: "",
        role: { label: role.name, value: role.id },
        active: data.active,
        loginType: data.email ? "email" : data.uid ? "uid" : null,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (type === "update") {
      getDetails(id);
    }
  }, [modalState, roleTypes]);

  function isValidEmail(email) {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return !regex.test(String(email));
  }

  const checkValidation = () => {
    const errors = {};
    const passwordErrors = validatePasswordChecker(userDetails?.password?.trim(), passwordPolicy);
    const validations = [
      { field: "name", condition: userDetails?.name?.trim().length === 0, message: "Kindly fill a valid name." },
      { field: "role", condition: !userDetails?.role, message: "Kindly select role." },
      {
        field: "phone",
        condition: userDetails?.phone?.trim().length === 0 || userDetails.phoneNumberError,
        message: "Kindly fill a valid phone number.",
      },
      {
        field: "emailOrUID",
        condition:
          userDetails.loginType === "uid"
            ? userDetails?.emailOrUID?.trim().length === 0
            : isValidEmail(userDetails?.emailOrUID?.trim()),
        message: userDetails.loginType === "uid" ? "Kindly fill." : "Kindly fill a valid email Id.",
      },
      {
        field: "password",
        condition: userDetails.loginType === "uid" && passwordErrors,
        message: passwordErrors,
      },
    ];

    validations.forEach(({ field, condition, message }) => {
      if (condition) errors[field] = message;
    });

    setError((prev) => ({ ...prev, ...errors }));

    return Object.keys(errors).length > 0;
  };

  const handleCreateUser = async () => {
    try {
      setError({
        name: null,
        emailOrUID: "",
        phone: "",
        password: "",
        role: null,
      });
      if (checkValidation()) return;

      handleLoading(true);
      let bodyData = {
        name: userDetails?.name?.trim() || null,
        email: userDetails?.loginType === "email" ? userDetails?.emailOrUID?.trim() || null : null,
        uid: userDetails?.loginType === "uid" ? userDetails?.emailOrUID?.trim() || null : null,
        password: userDetails?.loginType === "uid" ? userDetails?.password?.trim() || null : null,
        avatar: userDetails?.avatar?.trim() || null,
        designation: userDetails?.designation?.trim() || null,
        phone: userDetails?.phone?.trim() || null,
        role: userDetails?.role?.value,
        active: userDetails?.active,
      };

      await apiClient.post(`${configData.config.UNO_URL}${CREATE_USER}/${uuid}`, bodyData);
      if (handleRefreshTab) handleRefreshTab();
      handleCloseModal();
    } catch (error) {
      console.log(error);
      setError((pre) => ({ ...pre, submitError: error.response.data.message || "something went wrong" }));
    } finally {
      handleLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setError({
        name: null,
        emailOrUID: "",
        phone: "",
        password: "",
        role: null,
      });
      if (checkValidation()) return;
      handleLoading(true);
      let bodyData = {
        name: userDetails?.name?.trim() || null,
        avatar: userDetails?.avatar?.trim() || null,
        designation: userDetails?.designation?.trim() || null,
        phone: userDetails?.phone?.trim() || null,
        role: userDetails?.role?.value,
        active: userDetails?.active,
      };

      await apiClient.post(`${configData.config.UNO_URL}${UPDATE_USER}/${id}`, bodyData);
      if (handleRefreshTab) handleRefreshTab();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      console.log(error);
      setError((pre) => ({ ...pre, submitError: error.response.data.message || "something went wrong" }));
    } finally {
      handleLoading(false);
    }
  };

  const handleOpenModal = () => {
    const { handleRefreshTab, params } = modalState;
    openModal({ modalName: modalName.USER_CSV, handleRefreshTab, params });
  };

  const handleUpdateUserDetails = (name, value, shouldValidate) => {
    if (shouldValidate && VALIDATE_REGEX.test(value)) return;
    setUserDetails((pre) => ({ ...pre, [name]: value }))
  }

  const handleFormCreate = debounce(handleCreateUser, 500);
  const handleFormUpdate = debounce(handleUpdateUser, 500);

  return (
    <>
      <div className="user-add">
        <div className="profile-n-stuff">
          <div className="profile-view">
            <img
              className={`${userDetails.avatar && "userthumb"}`}
              src={userDetails.avatar || defaultLogo}
              referrerPolicy={"no-referrer"}
            />
          </div>
          <div className="action-btn d-flex align-items-center gap-3 ">
            <button className="gradient-btn" onClick={handleButtonClick}>
              <UploadIcon /> Upload Profile
              <input
                type="file"
                ref={fileInputRef}
                className="displayNone"
                onChange={handleLogoUpload}
                accept={ACCEPTED_IMAGE_TYPES.join(", ")}
              />
            </button>
            {error.file && <p>{error.file}</p>}

            {type === "update" && openChangePasswordModal && (
              <SimTooltip text="Change_password" placement="top">
                <div className="pass-btn background-gredient-circle" onClick={handleOpenChangePasswordModal}>
                  <PasswordKey />
                </div>
              </SimTooltip>
            )}
          </div>
        </div>
        <div className="container-fluid mt-4 mb-0 px-0 h-100">
          <div className="row group-add-data">
            <div className="col-md-6 h-100">
              <SimInputBar
                label="Full Name*"
                errorMessage={error.name}
                placeholder="Full Name"
                value={userDetails.name}
                name="name"
                onChange={(e) => handleUpdateUserDetails(e.target.name, e.target.value, true)}
              />
            </div>

            {type === "create" && (
              <>
                <div className="col-md-6 h-100">
                  <SimInputBar
                    errorMessage={error.emailOrUID}
                    label={`${userDetails.loginType === "uid" ? "UID*" : "Email Address*"}`}
                    placeholder={`${userDetails.loginType === "uid" ? "UID" : "Email Address"}`}
                    value={userDetails.emailOrUID}
                    name="emailOrUID"
                    onChange={(e) => handleUpdateUserDetails(e.target.name, userDetails.loginType === "uid" ? sanitizeWithRemoveSpace(e.target.value) : sanitizeString(e.target.value), true)}
                  />
                </div>

                {userDetails.loginType === "uid" && (
                  <div className="col-md-12 h-100">
                    <SimInputBar
                      errorMessage={error.password}
                      label="Password*"
                      value={userDetails.password}
                      placeholder="Password"
                      onChange={(e) => setUserDetails((pre) => ({ ...pre, password: e.target.value }))}
                    />
                  </div>
                )}
              </>
            )}

            <div className="col-md-6 h-100">
              {/* <SimInputBar
                label="Phone Number*"
                errorMessage={error.phone}
                placeholder="Phone Number"
                value={userDetails.phone}
                type="number"
                onChange={(e) => setUserDetails((pre) => ({ ...pre, phone: e.target.value }))}
              /> */}
              <div className="sim-input">
                <label htmlFor="phone">Phone Number*</label>
                <PhoneNumberInput
                  value={userDetails.phone}
                  onChange={(val, error) => {
                    setUserDetails((pre) => ({ ...pre, phone: val, phoneNumberError: error }));
                  }}
                />
                {error.phone && <p className="error-here">{error.phone}</p>}
              </div>
            </div>

            <div className="col-md-6 h-100">
              <SimInputBar
                label="Designation"
                placeholder="Designation"
                value={userDetails.designation}
                name="designation"
                onChange={(e) => handleUpdateUserDetails(e.target.name, e.target.value, true)}
              />
            </div>

            <div className="col-md-12 h-100 mb-2">
              <DropdownSearch
                label="Role Type*"
                error={error.role}
                selectProps={{
                  options: roleTypes,
                  value: userDetails.role,
                  isDisabled: type === "update" ? true : false,
                  onChange: (e) => setUserDetails((pre) => ({ ...pre, ["role"]: e })),
                }}
              />
            </div>

            {type === "create" && (
              <div className="col-md-6 h-100 mt-2 pe-3">
                <RadioOptions
                  parentLabel="Login Type*"
                  options={userLoginTypes}
                  selectedValue={userDetails.loginType}
                  inputClass="input-radio"
                  inputProps={{
                    onChange: (e) => setUserDetails((pre) => ({ ...pre, loginType: e.target.id })),
                  }}
                />
              </div>
            )}

            <div className="col-md-6 h-100 mt-2 ps-3">
              <SimToggleSwitch
                label="Status*"
                checked={userDetails.active}
                onChange={(e) => setUserDetails((pre) => ({ ...pre, ["active"]: !pre.active }))}
                toggleKeyNames="Active"
                ToggleKeyClass="active-inactive"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer mt-0">
        {!isMobileView ? (
          <>
            {type === "create" ? (
              <p className="link-text" onClick={handleOpenModal}>
                {" "}
                Upload User CSV{" "}
              </p>
            ) : (
              <p className="link-text"> </p>
            )}
          </>
        ) : (
          <p>{""}</p>
        )}
        <div>
          <div className="modal-footer-btns justify-content-end">
            <button className="cancel" onClick={handleCloseModal}>
              Cancel
            </button>
            <div>
              {type === "create" ? (
                <button className="save" onClick={handleFormCreate}>
                  {modalState.loading ? <BtnLoad /> : "Add"}
                </button>
              ) : (
                <button className="save" onClick={handleFormUpdate}>
                  {modalState.loading ? <BtnLoad /> : "Update"}
                </button>
              )}
            </div>
          </div>
          {error.submitError && <p className="text-danger m-0"> {error.submitError}</p>}
        </div>
      </div>
    </>
  );
};

export default UserAddData;
